// Country shape SVGs sourced from djaiss/mapsicon (https://github.com/djaiss/mapsicon).
// Mapsicon license: free to use with attribution; do not resell.
//
// The country list itself comes from the backend
// (`EditorialService.ListCountryCatalogs`). This file only carries the bits
// the backend doesn't know about: the flag emoji and the path to the country
// outline SVG, keyed by ISO-3 country code. Anything in the loader's
// `countries[]` array that doesn't have a presentation entry here renders
// without flag/map (functional but plain).

export interface CountryCatalog {
  /** URL slug — lowercase ISO-3 (e.g. "usa"). */
  code: string
  /** Country code as it appears on backend records (e.g. "USA"). */
  countryCode: string
  /** Human-readable name, sourced from CountryCatalog.displayName. */
  name: string
  /** Flag emoji — from COUNTRY_PRESENTATION, empty string if unmapped. */
  flag: string
  /**
   * Markdown description, sourced from CountryCatalog.description.
   * Renderers should pass this through MarkdownBody. Empty until editorial
   * adds content per country.
   */
  description: string
  /**
   * Set server-side for countries with a "coming soon" visibility when viewed
   * without internal access: the country renders as a clickable teaser card
   * and its catalog content (datasets, sub-pages) is withheld. The value is
   * the *kind* of coming-soon:
   *   - "docs": data is available, documentation is on the way.
   *   - "data": the datasets themselves are still being built.
   */
  comingSoon?: "docs" | "data"
  /** Path to the country shape SVG — from COUNTRY_PRESENTATION, empty if unmapped. */
  mapSvg: string
}

/**
 * Static presentation lookup keyed by ISO-3 country code (uppercase, as the
 * backend stores it).
 *
 * `flag` and `mapSvg` are pure presentation — the backend will never know
 * about them. `displayName` is the **fallback** human name used when the
 * backend isn't reachable (e.g. before unacat PR #2274 lands — see
 * docsCatalog.server.ts). Once `EditorialService.ListCountryCatalogs` is
 * available on the catalog gRPC server, `CountryCatalog.displayName` from
 * the backend takes precedence and these fallback strings are unused.
 *
 * Countries returned by the backend without a presentation entry render
 * without a flag or map outline (still functional, just plain).
 */
export const COUNTRY_PRESENTATION: Record<
  string,
  { flag: string; mapSvg: string; displayName: string }
> = {
  USA: { flag: "🇺🇸", mapSvg: "/maps/us.svg", displayName: "United States" },
  EST: { flag: "🇪🇪", mapSvg: "/maps/ee.svg", displayName: "Estonia" },
  NOR: { flag: "🇳🇴", mapSvg: "/maps/no.svg", displayName: "Norway" },
  SWE: { flag: "🇸🇪", mapSvg: "/maps/se.svg", displayName: "Sweden" },
  FIN: { flag: "🇫🇮", mapSvg: "/maps/fi.svg", displayName: "Finland" },
  GBR: { flag: "🇬🇧", mapSvg: "/maps/gb.svg", displayName: "United Kingdom" },
}

/**
 * Themable country shape rendered via CSS mask, so `text-*` classes set the
 * fill via `bg-current` on the underlying div. Falls back to an invisible
 * placeholder if the country has no mapSvg in COUNTRY_PRESENTATION.
 */
export function CountryShape({
  catalog,
  className,
}: {
  catalog: CountryCatalog
  className?: string
}) {
  if (!catalog.mapSvg) {
    return <div role="img" aria-label={`${catalog.name} outline`} className={className} />
  }
  return (
    <div
      role="img"
      aria-label={`${catalog.name} outline`}
      className={`bg-current ${className ?? ""}`}
      style={{
        WebkitMaskImage: `url(${catalog.mapSvg})`,
        WebkitMaskRepeat: "no-repeat",
        WebkitMaskPosition: "center",
        WebkitMaskSize: "contain",
        maskImage: `url(${catalog.mapSvg})`,
        maskRepeat: "no-repeat",
        maskPosition: "center",
        maskSize: "contain",
      }}
    />
  )
}
